import { CommonSwitch } from '@/components/common/switch';
import { ScreenContext } from '@/contexts/ScreenContext';
import { SettingMenuContext } from '@/contexts/SettingMenuContext';
import { BetViewMode } from '@/hooks/useGlobalData';
import { useIsLobby } from '@/hooks/useIsLobby';
import { Badge } from '@mui/material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import {
    Dispatch,
    SetStateAction,
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AudioPlayContext } from '../../contexts/AudioPlayContext';
import { Footer as LobbyFooter } from '../lobby/components/Footer';
import { LiteIcon } from '../lobby/icon';
import { getMainUserState } from '../main/selector';
import { Footer as InGameFooter } from '../rwdGame/components/Footer';
import { RwdSvgIcon } from '../rwdGame/components/RwdSvgIcon/RwdSvgIcon';
import { RwdTypography } from '../rwdGame/components/RwdTypography';
import { UserInformation } from '../userInformation';
import { BetRecord } from './components/BetRecord';
import { ChangePassword } from './components/ChangePassword';
import { DialogHeader } from './components/DialogHeader';
import { FeedbackForm } from './components/feedback';
import { GameAgreement } from './components/gameAgreement';
import { GameRules } from './components/gameRules';
import { GiftRecord } from './components/giftRecord/GiftRecord';
import { Language } from './components/language';
import { MemberReport } from './components/memberReport/MemberReport';
import { SoundSetting } from './components/soundSetting';
import { CustomTooltip } from './components/tooltip';
import { useDialog, useDialogState } from './hook';
import './index.scss';
import {
    IcoBetRecord,
    IcoBetViewA,
    IcoBetViewADim,
    IcoBetViewB,
    IcoBetViewBDim,
    IcoBetViewSwitch,
    IcoChangePassword,
    IcoChatRoom,
    IcoChecked,
    IcoExploreRewards,
    IcoFeedback,
    IcoFullScreen,
    IcoGameAgreement,
    IcoGameRules,
    IcoGiftRecord,
    IcoLanguage,
    IcoLite,
    IcoLiteTooltips,
    IcoLogout,
    IcoMemberReport,
    IcoSettingMenu,
    IcoSettingMenuInGame,
    IcoSound,
} from './svg';

const iconMap: { [key: string]: JSX.Element } = {
    'promotion_tool.explore_rewards': (
        <IcoExploreRewards className="list-icon" />
    ),
    'menu.bet_record': <IcoBetRecord className="list-icon" />,
    'menu.gift_record': <IcoGiftRecord className="list-icon" />,
    'menu.member_report': <IcoMemberReport className="list-icon" />,
    'menu.game_rules': <IcoGameRules className="list-icon" />,
    'menu.language': <IcoLanguage className="list-icon" />,
    'menu.sound': <IcoSound className="list-icon" />,
    'menu.agreement': <IcoGameAgreement className="list-icon" />,
    'menu.feedback': <IcoFeedback className="list-icon" />,
    'menu.change_password': <IcoChangePassword className="list-icon" />,
};

type IconSettingProps = {
    lite: boolean;
    lobby: boolean;
};

const IconSetting = (props: IconSettingProps) => {
    return (
        <>
            {props.lite ? (
                <Badge
                    className="setting-button-badge"
                    badgeContent={
                        <LiteIcon
                            className="setting-button-lite"
                            width={'100%'}
                            height={'100%'}
                        />
                    }
                >
                    <div className="setting-button">
                        {props.lobby ? (
                            <IcoSettingMenu />
                        ) : (
                            <IcoSettingMenuInGame />
                        )}
                    </div>
                </Badge>
            ) : (
                <div className="setting-button">
                    {props.lobby ? (
                        <IcoSettingMenu />
                    ) : (
                        <IcoSettingMenuInGame />
                    )}
                </div>
            )}
        </>
    );
};

type ListIconsProps = {
    labelKey: string;
    onClick: () => void;
};

const ListIcons = (props: ListIconsProps) => {
    const { t } = useTranslation();

    return (
        <IconButton
            className="setting-cell-container"
            disableRipple={true}
            onClick={props.onClick}
        >
            {iconMap[props.labelKey]}
            <RwdTypography stepIndex={2} landscapeStepIndex={3}>
                {t(props.labelKey)}
            </RwdTypography>
        </IconButton>
    );
};

export type SettingDialogProps = {
    setIsOpen: Dispatch<SetStateAction<boolean>>;
};

export const DialogContext = createContext({} as useDialogState);
const { Provider: DialogProvider } = DialogContext;

export const Setting = () => {
    const {
        isChatPanelEnabled,
        setIsChatPanelEnabled,
        showOtherPlayerGift,
        setShowOtherPlayerGift,
        lobbyGiftEnable,
        isChatRoomOn,
        ChatEnabled,
        setHostId,
        reference,
        data: settingMenuData,
        isLite,
        setSwitchLite,
        switchLite,
        hideFullScreen,
        isFullScreen,
        toggleFullScreen,
        drawerOpen,
        setDrawerOpen,
        closeDrawer,
        tooltipLiteModeOpen,
        setTooltipLiteModeOpen,
        tooltipBetAreaPositionOpen,
        setTooltipBetAreaPositionOpen,
        isGiftRecordOpen,
        setIsGiftRecordOpen,
        isBetRecordOpen,
        setIsBetRecordOpen,
        isMemberReportOpen,
        setIsMemberReportOpen,
        isGameRulesOpen,
        setIsGameRulesOpen,
        isLanguageOpen,
        setIsLanguageOpen,
        isSoundOpen,
        setIsSoundOpen,
        isAgreementOpen,
        setIsAgreementOpen,
        isFeedBackOpen,
        setIsFeedBackOpen,
        isChangePasswordOpen,
        setIsChangePasswordOpen,
        showBack,
        showBackWeb,
        switchZoom,
        setSwitchBetView,
        enableBetView,
        originBetViewStyle,
        reverseBetViewStyle,
        onClickMenuItem,
        onClickSetting,
        onClickBackToOldClient,
        logout,
    } = useContext(SettingMenuContext);
    const params = useParams();
    const hostId = Number(params.hostId);
    const isLobby = useIsLobby();
    const { IsDemo } = useSelector(getMainUserState);
    const { t, i18n } = useTranslation();
    const [list, setList] = useState(<></>);
    const { playButtonAudio } = useContext(AudioPlayContext);
    const { isApple, isLandscape, isMobile } = useContext(ScreenContext);
    const dialogOpenRef = useRef<boolean>(false);
    const setDialogOpenRef = useRef<Dispatch<SetStateAction<boolean>>>();
    const [selectedButton, setSelectedButton] = useState('');
    const [activatedDialog, setActivatedDialog] = useState(<></>);
    const controller = useDialog();

    useEffect(() => {
        setHostId(hostId);
    }, [hostId]);

    useEffect(() => {
        const items = Object.values(settingMenuData.list).filter(
            item => item !== ''
        );
        const fillBlank = items.length % 2 > 0;
        setList(
            <>
                {items.map((item, i) => (
                    <Grid
                        item
                        key={`setting-cell-${i}`}
                        className="setting-cell"
                    >
                        <ListIcons
                            labelKey={item}
                            onClick={() => {
                                playButtonAudio();
                                setSelectedButton(item);
                                setDrawerOpen(false);
                                onClickMenuItem(item);
                            }}
                        />
                    </Grid>
                ))}
                {!isLandscape && fillBlank && (
                    <Grid item className="setting-cell place-holder" />
                )}
            </>
        );
    }, [isLandscape, i18n.language]);

    useEffect(() => {
        // if(!dialogOpen) return;
        let dialogChildren = <></>;
        switch (selectedButton) {
            case 'promotion_tool.explore_rewards':
                return;
            case 'menu.gift_record':
                dialogOpenRef.current = isGiftRecordOpen;
                setDialogOpenRef.current = setIsGiftRecordOpen;
                dialogChildren = <GiftRecord setIsOpen={setIsGiftRecordOpen} />;
                break;
            case 'menu.bet_record':
                dialogOpenRef.current = isBetRecordOpen;
                setDialogOpenRef.current = setIsBetRecordOpen;
                dialogChildren = <BetRecord setIsOpen={setIsBetRecordOpen} />;
                break;
            case 'menu.member_report':
                dialogOpenRef.current = isMemberReportOpen;
                setDialogOpenRef.current = setIsMemberReportOpen;
                dialogChildren = <MemberReport />;
                break;
            case 'menu.game_rules':
                dialogOpenRef.current = isGameRulesOpen;
                setDialogOpenRef.current = setIsGameRulesOpen;
                dialogChildren = <GameRules setIsOpen={setIsGameRulesOpen} />;
                break;
            case 'menu.language':
                dialogOpenRef.current = isLanguageOpen;
                setDialogOpenRef.current = setIsLanguageOpen;
                dialogChildren = <Language setIsOpen={setIsLanguageOpen} />;
                break;
            case 'menu.sound':
                dialogOpenRef.current = isSoundOpen;
                setDialogOpenRef.current = setIsSoundOpen;
                dialogChildren = <SoundSetting setIsOpen={setIsSoundOpen} />;
                break;
            case 'menu.agreement':
                dialogOpenRef.current = isAgreementOpen;
                setDialogOpenRef.current = setIsAgreementOpen;
                dialogChildren = <GameAgreement />;
                break;
            case 'menu.feedback':
                dialogOpenRef.current = isFeedBackOpen;
                setDialogOpenRef.current = setIsFeedBackOpen;
                dialogChildren = <FeedbackForm setIsOpen={setIsFeedBackOpen} />;
                break;
            case 'menu.change_password':
                dialogOpenRef.current = isChangePasswordOpen;
                setDialogOpenRef.current = setIsChangePasswordOpen;
                dialogChildren = (
                    <ChangePassword setIsOpen={setIsChangePasswordOpen} />
                );
                break;
        }

        const dialog = (
            <Dialog
                open={dialogOpenRef.current}
                onClose={() => {
                    if (setDialogOpenRef.current)
                        setDialogOpenRef.current(false);
                    playButtonAudio();
                }}
                disableEscapeKeyDown
                fullScreen
            >
                <Box
                    className="dialog-content-container"
                    sx={{
                        overflow: isMobile ? 'auto' : 'hidden',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
                        },
                        '&-ms-overflow-style': {
                            display: 'none', // Hide the scrollbar for IE
                        },
                    }}
                >
                    <Box className="dialog-content">
                        <DialogHeader
                            title={selectedButton}
                            dialogOpen={setDialogOpenRef.current}
                        />
                        {dialogChildren}
                        <Box
                            position={'fixed'}
                            bottom={0}
                            width={'calc(var(--vw) * 100)'}
                            className={'footer-container'}
                        >
                            {hostId ? (
                                <InGameFooter hostId={hostId} />
                            ) : (
                                <LobbyFooter />
                            )}
                        </Box>
                    </Box>
                </Box>
            </Dialog>
        );

        setActivatedDialog(dialog);
    }, [
        isBetRecordOpen,
        isGiftRecordOpen,
        isMemberReportOpen,
        isGameRulesOpen,
        isLanguageOpen,
        isSoundOpen,
        isAgreementOpen,
        isFeedBackOpen,
        isChangePasswordOpen,
        selectedButton,
    ]);

    return (
        <DialogProvider value={controller}>
            <IconButton
                sx={{
                    aspectRatio: 1 / 1,
                    padding: 0,
                }}
                onClick={() => {
                    playButtonAudio();
                    onClickSetting();
                }}
            >
                <IconSetting lite={isLite} lobby={isLobby} />
            </IconButton>

            {/* setting menu */}
            <Drawer
                variant="temporary"
                anchor="right"
                open={drawerOpen}
                className="setting-drawer"
                onClose={() => closeDrawer()}
            >
                <List className="setting-list">
                    <UserInformation isHeader={false} />
                    <Box className="setting-cell-list-viewport">
                        <Grid container className="setting-cell-list">
                            {list}
                        </Grid>
                        <Grid className="switch-container">
                            {!IsDemo && (
                                <CustomTooltip
                                    title={
                                        <RwdTypography
                                            stepIndex={
                                                reference.tooltip
                                                    .stepIndexPortrait
                                            }
                                            landscapeStepIndex={
                                                reference.tooltip
                                                    .stepIndexLandscape
                                            }
                                            color={'#A6A6A6'}
                                        >
                                            {t('setting.lite.tooltip')}
                                        </RwdTypography>
                                    }
                                    placement="bottom"
                                    open={tooltipLiteModeOpen}
                                    slotProps={{
                                        popper: {
                                            modifiers: [
                                                {
                                                    name: 'offset',
                                                    options: {
                                                        offset: [0, 0],
                                                    },
                                                },
                                            ],
                                        },
                                    }}
                                >
                                    <Box className="switch-info">
                                        <Box
                                            display={'flex'}
                                            flex={1}
                                            alignItems={'center'}
                                        >
                                            <RwdSvgIcon
                                                portraitIconSize={
                                                    reference.switch
                                                        .icoSizePortrait
                                                }
                                                landscapeIconSize={
                                                    reference.switch
                                                        .icoSizeLandscape
                                                }
                                            >
                                                <IcoLite
                                                    width={'100%'}
                                                    height={'100%'}
                                                />
                                            </RwdSvgIcon>
                                            <RwdTypography
                                                stepIndex={
                                                    reference.switch
                                                        .stepIndexPortrait
                                                }
                                                landscapeStepIndex={
                                                    reference.switch
                                                        .stepIndexLandscape
                                                }
                                                className="switch-label"
                                            >
                                                {t('menu.lite')}
                                            </RwdTypography>
                                            <IconButton
                                                className="btn-tooltip"
                                                onClick={() => {
                                                    setTooltipLiteModeOpen(
                                                        !tooltipLiteModeOpen
                                                    );
                                                }}
                                                onMouseEnter={() => {
                                                    setTooltipLiteModeOpen(
                                                        true
                                                    );
                                                }}
                                                onMouseLeave={() => {
                                                    setTooltipLiteModeOpen(
                                                        false
                                                    );
                                                }}
                                            >
                                                <RwdSvgIcon
                                                    portraitIconSize={
                                                        reference.switch
                                                            .tooltipSizePortrait
                                                    }
                                                    landscapeIconSize={
                                                        reference.switch
                                                            .tooltipSizeLandscape
                                                    }
                                                >
                                                    <IcoLiteTooltips
                                                        width={'100%'}
                                                        height={'100%'}
                                                    />
                                                </RwdSvgIcon>
                                            </IconButton>
                                        </Box>
                                        <CommonSwitch
                                            sx={{
                                                zoom: switchZoom,
                                            }}
                                            checked={switchLite}
                                            onChange={() => {
                                                playButtonAudio();
                                                setSwitchLite(!switchLite);
                                            }}
                                        />
                                    </Box>
                                </CustomTooltip>
                            )}

                            {!isApple && !hideFullScreen && (
                                <Box className="switch-info">
                                    <Box
                                        display={'flex'}
                                        flex={1}
                                        alignItems={'center'}
                                    >
                                        <RwdSvgIcon
                                            portraitIconSize={
                                                reference.switch.icoSizePortrait
                                            }
                                            landscapeIconSize={
                                                reference.switch
                                                    .icoSizeLandscape
                                            }
                                        >
                                            <IcoFullScreen
                                                width={'100%'}
                                                height={'100%'}
                                            />
                                        </RwdSvgIcon>
                                        <RwdTypography
                                            stepIndex={
                                                reference.switch
                                                    .stepIndexPortrait
                                            }
                                            landscapeStepIndex={
                                                reference.switch
                                                    .stepIndexLandscape
                                            }
                                            className="switch-label"
                                        >
                                            {t('menu.full_screen')}
                                        </RwdTypography>
                                    </Box>
                                    <CommonSwitch
                                        sx={{ zoom: switchZoom }}
                                        checked={isFullScreen}
                                        onChange={() => {
                                            playButtonAudio();
                                            toggleFullScreen();
                                            // setDrawerOpen(false);
                                            closeDrawer();
                                        }}
                                    />
                                </Box>
                            )}

                            {lobbyGiftEnable && (
                                <Box className="switch-info">
                                    <Box
                                        display={'flex'}
                                        flex={1}
                                        alignItems={'center'}
                                    >
                                        <RwdSvgIcon
                                            portraitIconSize={
                                                reference.switch.icoSizePortrait
                                            }
                                            landscapeIconSize={
                                                reference.switch
                                                    .icoSizeLandscape
                                            }
                                        >
                                            <IcoGiftRecord
                                                width={'100%'}
                                                height={'100%'}
                                            />
                                        </RwdSvgIcon>
                                        <RwdTypography
                                            stepIndex={
                                                reference.switch
                                                    .stepIndexPortrait
                                            }
                                            landscapeStepIndex={
                                                reference.switch
                                                    .stepIndexLandscape
                                            }
                                            className="switch-label"
                                        >
                                            {t('toggle_gift_animation')}
                                        </RwdTypography>
                                    </Box>
                                    <CommonSwitch
                                        sx={{
                                            zoom: switchZoom,
                                        }}
                                        checked={showOtherPlayerGift}
                                        onChange={() => {
                                            playButtonAudio();
                                            setShowOtherPlayerGift(
                                                !showOtherPlayerGift
                                            );
                                        }}
                                    />
                                </Box>
                            )}

                            {isChatRoomOn && (
                                <Box className="switch-info">
                                    <Box
                                        display={'flex'}
                                        flex={1}
                                        alignItems={'center'}
                                    >
                                        <RwdSvgIcon
                                            portraitIconSize={
                                                reference.switch.icoSizePortrait
                                            }
                                            landscapeIconSize={
                                                reference.switch
                                                    .icoSizeLandscape
                                            }
                                        >
                                            <IcoChatRoom
                                                width={'100%'}
                                                height={'100%'}
                                            />
                                        </RwdSvgIcon>
                                        <RwdTypography
                                            stepIndex={
                                                reference.switch
                                                    .stepIndexPortrait
                                            }
                                            landscapeStepIndex={
                                                reference.switch
                                                    .stepIndexLandscape
                                            }
                                            className="switch-label"
                                        >
                                            {t('chat_room.chat_room_name')}
                                        </RwdTypography>
                                    </Box>
                                    <CommonSwitch
                                        sx={{
                                            zoom: switchZoom,
                                            opacity: ChatEnabled ? 1 : 0.5,
                                        }}
                                        checked={isChatPanelEnabled}
                                        disabled={!ChatEnabled}
                                        onChange={() => {
                                            playButtonAudio();
                                            setIsChatPanelEnabled(
                                                !isChatPanelEnabled
                                            );
                                        }}
                                    />
                                </Box>
                            )}
                        </Grid>

                        <Grid className="switch-container switch-bet-area-container bet-view-layout">
                            <CustomTooltip
                                title={
                                    <RwdTypography
                                        stepIndex={
                                            reference.tooltip.stepIndexPortrait
                                        }
                                        landscapeStepIndex={
                                            reference.tooltip.stepIndexLandscape
                                        }
                                        color={'#A6A6A6'}
                                    >
                                        {t('setting.bet_area_position.tooltip')}
                                    </RwdTypography>
                                }
                                placement="bottom"
                                open={tooltipBetAreaPositionOpen}
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: 'offset',
                                                options: {
                                                    offset: [0, 0],
                                                },
                                            },
                                        ],
                                    },
                                }}
                            >
                                <Box className="switch-info">
                                    <Box
                                        display={'flex'}
                                        flex={1}
                                        alignItems={'center'}
                                    >
                                        <Box>
                                            <RwdSvgIcon
                                                portraitIconSize={
                                                    reference.switch
                                                        .icoSizePortrait
                                                }
                                                landscapeIconSize={
                                                    reference.switch
                                                        .icoSizeLandscape
                                                }
                                            >
                                                <IcoBetViewSwitch
                                                    width={'100%'}
                                                    height={'100%'}
                                                />
                                            </RwdSvgIcon>
                                        </Box>
                                        <RwdTypography
                                            stepIndex={
                                                reference.switch
                                                    .stepIndexPortrait
                                            }
                                            landscapeStepIndex={
                                                reference.switch
                                                    .stepIndexLandscape
                                            }
                                            className="switch-label"
                                        >
                                            {t('menu.bet_area_position')}
                                        </RwdTypography>
                                        <IconButton
                                            className="btn-tooltip"
                                            onClick={() => {
                                                setTooltipBetAreaPositionOpen(
                                                    !tooltipBetAreaPositionOpen
                                                );
                                            }}
                                            onMouseEnter={() => {
                                                setTooltipBetAreaPositionOpen(
                                                    true
                                                );
                                            }}
                                            onMouseLeave={() => {
                                                setTooltipBetAreaPositionOpen(
                                                    false
                                                );
                                            }}
                                        >
                                            <RwdSvgIcon
                                                portraitIconSize={
                                                    reference.switch
                                                        .tooltipSizePortrait
                                                }
                                                landscapeIconSize={
                                                    reference.switch
                                                        .tooltipSizeLandscape
                                                }
                                            >
                                                <IcoLiteTooltips
                                                    width={'100%'}
                                                    height={'100%'}
                                                />
                                            </RwdSvgIcon>
                                        </IconButton>
                                    </Box>
                                    <Box className="bet-view-container">
                                        <IconButton
                                            className={originBetViewStyle}
                                            disableRipple={true}
                                            onClick={() => {
                                                if (
                                                    !isLandscape &&
                                                    enableBetView
                                                ) {
                                                    setSwitchBetView(
                                                        BetViewMode.Original
                                                    );
                                                    playButtonAudio();
                                                    closeDrawer(
                                                        BetViewMode.Original
                                                    );
                                                }
                                            }}
                                        >
                                            <RwdSvgIcon
                                                portraitIconSize={
                                                    reference.betview
                                                        .icoSizePortrait
                                                }
                                                landscapeIconSize={
                                                    reference.betview
                                                        .icoSizeLandscape
                                                }
                                            >
                                                {!isLandscape &&
                                                enableBetView ? (
                                                    <IcoBetViewA
                                                        width={'100%'}
                                                        height={'100%'}
                                                    />
                                                ) : (
                                                    <IcoBetViewADim
                                                        width={'100%'}
                                                        height={'100%'}
                                                    />
                                                )}
                                            </RwdSvgIcon>
                                            <Box className="button-checked">
                                                <RwdSvgIcon
                                                    portraitIconSize={
                                                        reference.betview
                                                            .checkBoxSizePortrait
                                                    }
                                                    landscapeIconSize={
                                                        reference.betview
                                                            .checkBoxSizeLandscape
                                                    }
                                                >
                                                    <IcoChecked
                                                        width={'100%'}
                                                        height={'100%'}
                                                    />
                                                </RwdSvgIcon>
                                            </Box>
                                        </IconButton>
                                        <IconButton
                                            className={reverseBetViewStyle}
                                            disableRipple={true}
                                            onClick={() => {
                                                if (
                                                    !isLandscape &&
                                                    enableBetView
                                                ) {
                                                    setSwitchBetView(
                                                        BetViewMode.Reverse
                                                    );
                                                    playButtonAudio();
                                                    closeDrawer(
                                                        BetViewMode.Reverse
                                                    );
                                                }
                                            }}
                                        >
                                            <RwdSvgIcon
                                                portraitIconSize={
                                                    reference.betview
                                                        .icoSizePortrait
                                                }
                                                landscapeIconSize={
                                                    reference.betview
                                                        .icoSizeLandscape
                                                }
                                            >
                                                {!isLandscape &&
                                                enableBetView ? (
                                                    <IcoBetViewB
                                                        width={'100%'}
                                                        height={'100%'}
                                                    />
                                                ) : (
                                                    <IcoBetViewBDim
                                                        width={'100%'}
                                                        height={'100%'}
                                                    />
                                                )}
                                            </RwdSvgIcon>
                                            <Box className="button-checked">
                                                <RwdSvgIcon
                                                    portraitIconSize={
                                                        reference.betview
                                                            .checkBoxSizePortrait
                                                    }
                                                    landscapeIconSize={
                                                        reference.betview
                                                            .checkBoxSizeLandscape
                                                    }
                                                >
                                                    <IcoChecked
                                                        width={'100%'}
                                                        height={'100%'}
                                                    />
                                                </RwdSvgIcon>
                                            </Box>
                                        </IconButton>
                                    </Box>
                                </Box>
                            </CustomTooltip>
                        </Grid>
                    </Box>
                </List>
                <Grid
                    className="logout-container"
                    sx={{
                        justifyContent:
                            showBack || showBackWeb
                                ? 'space-between'
                                : 'flex-end',
                    }}
                >
                    {(showBack || showBackWeb) && (
                        <IconButton
                            className="setting-logout"
                            disableRipple={true}
                            onClick={() => {
                                playButtonAudio();
                                onClickBackToOldClient();
                            }}
                        >
                            <RwdTypography
                                stepIndex={reference.logout.stepIndexPortrait}
                                landscapeStepIndex={
                                    reference.logout.stepIndexLandscape
                                }
                                className="text-underline"
                            >
                                {t('new_ui.back_to_old')}
                            </RwdTypography>
                        </IconButton>
                    )}

                    {settingMenuData.logout != '' && (
                        <IconButton
                            className="setting-logout"
                            disableRipple={true}
                            onClick={() => {
                                playButtonAudio();
                                logout();
                            }}
                        >
                            <RwdSvgIcon
                                portraitIconSize={
                                    reference.logout.icoSizePortrait
                                }
                                landscapeIconSize={
                                    reference.logout.icoSizeLandscape
                                }
                            >
                                <IcoLogout width={'100%'} height={'100%'} />
                            </RwdSvgIcon>
                            <RwdTypography
                                stepIndex={reference.logout.stepIndexPortrait}
                                landscapeStepIndex={
                                    reference.logout.stepIndexLandscape
                                }
                            >
                                {t(settingMenuData.logout)}
                            </RwdTypography>
                        </IconButton>
                    )}
                </Grid>
            </Drawer>
            {activatedDialog}
        </DialogProvider>
    );
};
